import React, { useEffect, useMemo, useRef, useState } from "react";

const streetsImages = Array.from(
  { length: 10 },
  (_, index) => `/images/streets/${index + 1}.jpg`
);
const streetsThumbs = Array.from(
  { length: 10 },
  (_, index) => `/images/streets/thumbs/${index + 1}.jpg`
);

const cosmicCreaturesImages = Array.from(
  { length: 14 },
  (_, index) => `/images/cosmiccreatures/${index + 1}.jpg`
);
const cosmicCreaturesThumbs = Array.from(
  { length: 14 },
  (_, index) => `/images/cosmiccreatures/thumbs/${index + 1}.jpg`
);

const bonkersImages = Array.from(
  { length: 8 },
  (_, index) => `/images/bonkers/${index + 1}.jpg`
);
const bonkersThumbs = Array.from(
  { length: 8 },
  (_, index) => `/images/bonkers/thumbs/${index + 1}.jpg`
);

const mechagasmNumbers = [1, 4, 5, 6, 8, 9, 13, 14, 15, 16];
const mechagasmImages = mechagasmNumbers.map(
  (number) => `/images/mechagasm/${number}.jpg`
);
const mechagasmThumbs = mechagasmNumbers.map(
  (number) => `/images/mechagasm/thumbs/${number}.jpg`
);

const motionSicknessPreview = "/images/motionthumb2.mp4";
const motionSicknessEmbed =
  "https://player.vimeo.com/video/1192984226?badge=0&autopause=0&player_id=0&app_id=58479";

const machinePaintRecursionPreview = "/images/recurthumb.mp4";
const machinePaintRecursionEmbed =
  "https://player.vimeo.com/video/1193004290?badge=0&autopause=0&player_id=0&app_id=58479";

const sets = [
  {
    title: "STREETS",
    code: "Mad-26",
    tags: ["raw", "paint", "street"],
    desc: "B0NK creatures loose in the real world",
    about:
      "B0NK bodies spill through real and remixed landscapes photographed in Thailand, turning liminal spaces into unstable zones of cartoon flesh and machine madness.",
    images: streetsImages,
    thumbs: streetsThumbs,
  },
  {
    title: "COSMIC CREATURES",
    code: "Mad-26",
    tags: ["raw", "canvas", "creatures"],
    desc: "Cosmic creatures on canvas",
    about:
      "Developed from machine-born forms generated from B0NK's own datasets, the figures are dragged back into the physical world through acrylic paint and mixed media.",
    images: cosmicCreaturesImages,
    thumbs: cosmicCreaturesThumbs,
  },
  {
    title: "BONKERS",
    code: "Mad-26",
    tags: ["paper", "monsters", "chaos"],
    desc: "Monsters, Group scenes and ecstatic body chaos on paper and canvas",
    about:
      "Using charcoal as the main tool, feeling the ancient and B0NKing the future, these group scenes push deeper into the absurd and sublime.",
    images: bonkersImages,
    thumbs: bonkersThumbs,
  },
  {
    title: "MECHAGASM",
    code: "MdSynth-01",
    tags: ["machine", "synthetic", "flesh"],
    desc: "Synthetic forms born from the Mechagasm",
    about:
      "Trained on B0NK paintings and drawings, the machine takes B0NK logic and births visceral, intimate, beautiful, and violent imagery.",
    images: mechagasmImages,
    thumbs: mechagasmThumbs,
  },
  {
    title: "MOTION SICKNESS",
    code: "MV-01",
    tags: ["video", "moving image", "signal"],
    desc: "Animations from the Mechagasm",
    about:
      "Animated images, using a already surpassed video model, influence by a lora trained on B0NK's early experiments with AI video.",
    type: "video",
    preview: motionSicknessPreview,
    embed: motionSicknessEmbed,
    aspectRatio: "100 / 152.04",
    images: [],
    thumbs: [],
  },
  {
    title: "MACHINE PAINT RECURSION",
    code: "MPR-01",
    tags: ["process", "machine", "painting"],
    desc: "Madrid / Mobb Art Club Residency 2026 Documentation",
    about:
      "Machine Paint Recursion documents the studio loop behind the work: painting entering the machine, synthetic images returning, and the hand feeding the signal back into physical form.",
    type: "video",
    preview: machinePaintRecursionPreview,
    embed: machinePaintRecursionEmbed,
    aspectRatio: "100 / 177.78",
    images: [],
    thumbs: [],
  },
];

function HeaderBanner() {
  return (
    <div className="relative mx-auto flex w-[min(1180px,94vw)] select-none items-center justify-center overflow-visible py-1">
      <h1
        data-text="B0NKCORE"
        className="mainGlitchTitle glitchTitle m-0 text-center text-[clamp(48px,13vw,155px)] font-black uppercase leading-[.78] tracking-[-.06em] text-[#ff3f9d] md:text-[clamp(70px,11vw,165px)] lg:text-[clamp(68px,9vw,138px)]"
      >
        B0NKCORE
      </h1>
    </div>
  );
}

function LoadingScreen({ progress, label = "Loading" }) {
  return (
    <div className="fixed inset-0 z-[999] grid place-items-center bg-white text-black">
      <div className="w-[min(760px,86vw)] text-center">
        <div className="relative mx-auto flex w-full items-center justify-center py-4">
          <div
            data-text="B0NKCORE"
            className="mainGlitchTitle glitchTitle text-center text-[clamp(50px,13vw,132px)] font-black uppercase leading-[.78] tracking-[-.06em] text-[#ff3f9d]"
          >
            B0NKCORE
          </div>
        </div>
        <div className="mx-auto mt-5 h-4 max-w-[620px] overflow-hidden border border-black bg-white p-[3px]">
          <div
            className="h-full bg-[#ff3f9d] transition-[width] duration-200"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="mx-auto mt-3 flex max-w-[620px] justify-between font-mono text-[10px] uppercase tracking-[.18em] text-zinc-500">
          <span>{label}</span>
          <span className="text-[#ff3f9d]">{progress}%</span>
        </div>
      </div>
    </div>
  );
}

function CensorBars({ tiny = false }) {
  return (
    <div className="pointer-events-none absolute inset-0 z-20 overflow-hidden">
      <div
        className={`absolute left-[10%] top-[18%] ${
          tiny ? "h-3" : "h-7"
        } w-[58%] -skew-x-12 overflow-hidden bg-black shadow-[0_10px_22px_rgba(0,0,0,.18)]`}
      >
        {!tiny && (
          <span className="absolute left-2 top-1 font-mono text-[10px] uppercase tracking-[.24em] text-white/75">
            CENSORED CENSORED CENSORED
          </span>
        )}
      </div>
      <div
        className={`absolute right-[9%] top-[46%] ${
          tiny ? "h-3" : "h-7"
        } w-[43%] skew-x-12 overflow-hidden bg-black shadow-[0_10px_22px_rgba(0,0,0,.18)]`}
      >
        {!tiny && (
          <span className="absolute left-2 top-1 font-mono text-[10px] uppercase tracking-[.24em] text-[#ff3f9d]">
            CENSORED CENSORED
          </span>
        )}
      </div>
      <div
        className={`absolute bottom-[19%] left-[18%] ${
          tiny ? "h-3" : "h-7"
        } w-[48%] -skew-x-6 overflow-hidden bg-black shadow-[0_10px_22px_rgba(0,0,0,.18)]`}
      />
      <div className="absolute left-0 top-[34%] h-4 w-full translate-x-4 bg-[#ff3f9d]/25 mix-blend-multiply blur-[1px]" />
      <div className="absolute left-0 bottom-[31%] h-3 w-full -translate-x-5 bg-black/10 mix-blend-multiply" />
    </div>
  );
}

function PlaceholderSurface({ active = false, tiny = false }) {
  return (
    <div
      className={`relative h-full w-full overflow-hidden bg-white ${
        tiny ? "" : "rounded-[2.2rem]"
      }`}
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_22%_18%,rgba(255,255,255,.98),transparent_15%),radial-gradient(circle_at_76%_26%,rgba(255,63,157,.17),transparent_18%),radial-gradient(circle_at_65%_72%,rgba(255,232,107,.16),transparent_16%),radial-gradient(circle_at_36%_70%,rgba(242,198,181,.22),transparent_18%),linear-gradient(135deg,#ffffff_0%,#fbfbfc_48%,#ffe8f2_100%)]" />
      <div className="absolute inset-[8%] border border-black/10 bg-[linear-gradient(135deg,rgba(255,255,255,.62),transparent_30%),linear-gradient(45deg,transparent_53%,rgba(255,63,157,.22)_54%,rgba(255,63,157,.22)_57%,transparent_58%),linear-gradient(135deg,transparent_49%,rgba(255,232,107,.24)_50%,rgba(255,232,107,.24)_53%,transparent_54%)]" />
      <div className="absolute left-[-4%] right-[-4%] top-[52%] h-5 -skew-x-12 bg-white/85 shadow-[0_0_20px_rgba(255,255,255,.7)]" />
      <CensorBars tiny={tiny} />
    </div>
  );
}

function ImageSurface({
  src,
  fallbackSrc,
  alt = "",
  active = false,
  tiny = false,
  fit = "cover",
  zoom = 1,
  pannable = false,
  position = "center center",
}) {
  const [failed, setFailed] = useState(false);
  const [fallbackFailed, setFallbackFailed] = useState(false);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);

  const dragStartRef = useRef({ x: 0, y: 0 });
  const panStartRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    setFailed(false);
    setFallbackFailed(false);
    setPan({ x: 0, y: 0 });
    setDragging(false);
  }, [src, fallbackSrc]);

  useEffect(() => {
    if (zoom <= 1) {
      setPan({ x: 0, y: 0 });
      setDragging(false);
    }
  }, [zoom]);

  const displaySrc = failed && fallbackSrc && !fallbackFailed ? fallbackSrc : src;

  if (!displaySrc || fallbackFailed) {
    return <PlaceholderSurface active={active} tiny={tiny} />;
  }

  const canPan = pannable && fit === "contain" && zoom > 1 && !tiny;

  const startDrag = (clientX, clientY) => {
    if (!canPan) return;
    setDragging(true);
    dragStartRef.current = { x: clientX, y: clientY };
    panStartRef.current = { ...pan };
  };

  const moveDrag = (clientX, clientY) => {
    if (!canPan || !dragging) return;

    const dx = clientX - dragStartRef.current.x;
    const dy = clientY - dragStartRef.current.y;

    setPan({
      x: panStartRef.current.x + dx,
      y: panStartRef.current.y + dy,
    });
  };

  const stopDrag = () => {
    setDragging(false);
  };

  return (
    <div
      className={`relative h-full w-full overflow-hidden bg-white ${
        tiny ? "" : "rounded-[2.2rem]"
      }`}
      onMouseMove={(event) => moveDrag(event.clientX, event.clientY)}
      onMouseUp={stopDrag}
      onMouseLeave={stopDrag}
      onTouchMove={(event) => {
        if (!event.touches[0]) return;
        if (canPan) event.preventDefault();
        moveDrag(event.touches[0].clientX, event.touches[0].clientY);
      }}
      onTouchEnd={stopDrag}
      style={{ touchAction: canPan ? "none" : "auto" }}
    >
      <img
        src={displaySrc}
        alt={alt}
        draggable={false}
        onError={() => {
          if (displaySrc === fallbackSrc) {
            setFallbackFailed(true);
          } else {
            setFailed(true);
          }
        }}
        onMouseDown={(event) => startDrag(event.clientX, event.clientY)}
        onTouchStart={(event) => {
          if (!event.touches[0]) return;
          startDrag(event.touches[0].clientX, event.touches[0].clientY);
        }}
        className={`h-full w-full select-none ${
          fit === "contain" ? "object-contain" : "object-cover"
        } transition-transform duration-75 ${
          canPan ? (dragging ? "cursor-grabbing" : "cursor-grab") : ""
        }`}
        style={{
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          transformOrigin: "center center",
          objectPosition: position,
        }}
        loading={tiny ? "lazy" : "eager"}
      />
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,transparent_0%,rgba(255,255,255,.05)_45%,rgba(0,0,0,.08)_100%)]" />
    </div>
  );
}

function LoopingPreviewVideo({ src, className = "" }) {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute("muted", "");
    video.setAttribute("playsinline", "");
    video.setAttribute("webkit-playsinline", "true");

    const tryPlay = () => {
      const promise = video.play();
      if (promise?.catch) promise.catch(() => {});
    };

    video.load();
    tryPlay();
    video.addEventListener("loadeddata", tryPlay);
    video.addEventListener("canplay", tryPlay);

    return () => {
      video.removeEventListener("loadeddata", tryPlay);
      video.removeEventListener("canplay", tryPlay);
    };
  }, [src]);

  return (
    <video
      ref={videoRef}
      key={src}
      src={src}
      muted
      loop
      autoPlay
      playsInline
      preload="auto"
      className={className}
    />
  );
}

export default function BonkcoreWebsite() {
  const [selected, setSelected] = useState(0);
  const [chosenSetIndex, setChosenSetIndex] = useState(null);
  const [viewer, setViewer] = useState(false);
  const [videoViewer, setVideoViewer] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [loadProgress, setLoadProgress] = useState(0);

  const featureRef = useRef(null);

  const visible = useMemo(() => sets, []);

  const preloadUrls = useMemo(() => {
    const urls = new Set();

    sets.forEach((set) => {
      (set.thumbs || []).forEach((src) => src && urls.add(src));

      if (set.type === "video") {
        if (set.preview) urls.add(set.preview);
      } else {
        (set.images || []).forEach((src) => src && urls.add(src));
      }
    });

    return Array.from(urls);
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!preloadUrls.length) {
      setLoadProgress(100);
      setIsLoading(false);
      return undefined;
    }

    let loaded = 0;

    const updateProgress = () => {
      loaded += 1;
      const nextProgress = Math.round((loaded / preloadUrls.length) * 100);

      if (!cancelled) {
        setLoadProgress(nextProgress);
      }

      if (!cancelled && loaded >= preloadUrls.length) {
        window.setTimeout(() => {
          if (!cancelled) setIsLoading(false);
        }, 250);
      }
    };

    preloadUrls.forEach((src) => {
      const isVideo = src.toLowerCase().endsWith(".mp4");

      if (isVideo) {
        const video = document.createElement("video");
        video.preload = "auto";
        video.muted = true;
        video.playsInline = true;
        video.onloadeddata = updateProgress;
        video.onerror = updateProgress;
        video.src = src;
        video.load();
        return;
      }

      const image = new Image();
      image.onload = updateProgress;
      image.onerror = updateProgress;
      image.src = src;
    });

    return () => {
      cancelled = true;
    };
  }, [preloadUrls]);

  const active = sets[selected];
  const isVideoSet = active.type === "video";
  const activeImages = active.images || [];
  const activeThumbs = active.thumbs || [];
  const activeImageCount = isVideoSet ? 1 : activeImages.length || 8;
  const currentImageIndex = activeImages.length
    ? Math.min(imageIndex, activeImages.length - 1)
    : 0;
  const mainHeroImage = isVideoSet ? null : activeImages[0] || null;
  const viewerImage = activeImages[currentImageIndex] || activeImages[0] || null;
  const hasUnlockedEnter = chosenSetIndex !== null;

  const scrollToFeature = () => {
    window.requestAnimationFrame(() => {
      featureRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const selectSet = (index) => {
    setSelected(index);
    setChosenSetIndex(index);
    setImageIndex(0);
    setZoom(1);
    scrollToFeature();
  };

  const chooseImage = (index) => {
    setImageIndex(index);
    setZoom(1);
  };

  const openSet = (index) => {
    const set = sets[index];
    setSelected(index);
    setChosenSetIndex(index);
    setImageIndex(0);
    setZoom(1);

    if (set.type === "video") {
      setVideoViewer(true);
      return;
    }

    setViewer(true);
  };

  const move = (direction) => {
    setImageIndex(
      (current) => (current + direction + activeImageCount) % activeImageCount
    );
    setZoom(1);
  };

  return (
    <div className="min-h-screen overflow-hidden bg-white text-[#111] selection:bg-[#ff3f9d] selection:text-white">
      {isLoading && <LoadingScreen progress={loadProgress} />}

      <style>{`
        .glitchTitle { text-shadow: 3px 0 #ffd7e8, -3px 0 #111, 0 0 24px rgba(255,63,157,.18); }
        .mainGlitchTitle {
          position: relative;
          display: inline-block;
          animation: titleGlitchStrobe .48s steps(2,end) infinite;
          filter: contrast(1.08);
        }
        .mainGlitchTitle::before,
        .mainGlitchTitle::after {
          content: attr(data-text);
          position: absolute;
          inset: 0;
          pointer-events: none;
          opacity: 0;
          mix-blend-mode: multiply;
        }
        .mainGlitchTitle::before {
          color: #111;
          text-shadow: -2px 0 #ff3f9d;
          animation: titleBlackSlice .62s steps(2,end) infinite;
        }
        .mainGlitchTitle::after {
          color: #ffd7e8;
          text-shadow: 2px 0 #111;
          animation: titlePinkSlice .37s steps(2,end) infinite;
        }
        @keyframes titleGlitchStrobe {
          0%, 100% {
            transform: translate(0,0) skewX(0deg);
            text-shadow: 3px 0 #ffd7e8, -3px 0 #111, 0 0 24px rgba(255,63,157,.18);
          }
          9% {
            transform: translate(-1px,1px) skewX(-1deg);
            text-shadow: 7px 0 #ffd7e8, -5px 0 #111, 0 0 30px rgba(255,63,157,.28);
          }
          10% {
            transform: translate(2px,-1px) skewX(1deg);
            text-shadow: -6px 0 #ffd7e8, 5px 0 #111, 0 0 18px rgba(255,63,157,.2);
          }
          11%, 38% { transform: translate(0,0) skewX(0deg); }
          39% { transform: translate(1px,0) skewX(2deg); }
          40% { transform: translate(-2px,1px) skewX(-2deg); }
          41%, 73% { transform: translate(0,0) skewX(0deg); }
          74% {
            transform: translate(3px,-1px) skewX(1.5deg);
            text-shadow: 10px 0 #ffd7e8, -7px 0 #111, 0 0 34px rgba(255,63,157,.34);
          }
          75% { transform: translate(-1px,1px) skewX(-1.5deg); }
          76% { transform: translate(0,0) skewX(0deg); }
        }
        @keyframes titleBlackSlice {
          0%, 7%, 100% { opacity: 0; transform: translate(0,0); clip-path: inset(0 0 100% 0); }
          8% { opacity: .9; transform: translate(12px,-2px); clip-path: inset(8% 0 72% 0); }
          10% { opacity: .75; transform: translate(-14px,2px); clip-path: inset(62% 0 18% 0); }
          12%, 44% { opacity: 0; }
          45% { opacity: .85; transform: translate(-10px,1px); clip-path: inset(34% 0 43% 0); }
          47% { opacity: 0; }
          82% { opacity: .8; transform: translate(15px,-1px); clip-path: inset(76% 0 6% 0); }
          84% { opacity: 0; }
        }
        @keyframes titlePinkSlice {
          0%, 16%, 100% { opacity: 0; transform: translate(0,0); clip-path: inset(0 0 100% 0); }
          17% { opacity: .85; transform: translate(-12px,1px); clip-path: inset(18% 0 62% 0); }
          18% { opacity: .95; transform: translate(16px,-2px); clip-path: inset(48% 0 30% 0); }
          20%, 56% { opacity: 0; }
          57% { opacity: .9; transform: translate(9px,2px); clip-path: inset(4% 0 78% 0); }
          58% { opacity: 0; }
          88% { opacity: .95; transform: translate(-18px,0); clip-path: inset(66% 0 16% 0); }
          90% { opacity: 0; }
        }
        @keyframes enterFlash {
          0%, 100% {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1) rotate(-1deg);
            box-shadow: 0 22px 60px rgba(0,0,0,.28), 0 0 0 8px rgba(255,63,157,.12);
          }
          48% {
            opacity: .82;
            transform: translate(-50%, -50%) scale(1.035) rotate(1deg);
            box-shadow: 0 26px 80px rgba(255,63,157,.28), 0 0 0 12px rgba(255,63,157,.18);
          }
          50% {
            opacity: 1;
            transform: translate(-50%, -50%) scale(.98) rotate(-2deg);
            box-shadow: 0 18px 50px rgba(0,0,0,.22), 0 0 0 5px rgba(255,63,157,.22);
          }
        }
        .desktopEnterButton { display: none; }
        @media (min-width: 768px) {
          .desktopEnterButton {
            display: grid !important;
            place-items: center !important;
            position: absolute !important;
            left: 50% !important;
            top: 50% !important;
            z-index: 45 !important;
            min-width: 210px !important;
            padding: 1.25rem 2.4rem !important;
            border: 2px solid rgba(255,63,157,.75) !important;
            background: rgba(0,0,0,.92) !important;
            color: white !important;
            font-size: 14px !important;
            animation: enterFlash 1.05s steps(2,end) infinite;
            backdrop-filter: blur(8px);
          }
          .desktopEnterButton:hover { background: #ff3f9d !important; color: white !important; }
        }
        @media (min-width: 768px) {
          .viewerShell { padding: .75rem !important; }
          .viewerBox { grid-template-rows: auto minmax(0,1fr) auto !important; }
          .viewerHeader { padding: .55rem .75rem !important; align-items: center !important; }
          .viewerHeaderInner { align-items: center !important; gap: 1rem !important; }
          .viewerTitle {
            font-size: clamp(30px, 5.4vw, 62px) !important;
            line-height: .78 !important;
            letter-spacing: -.075em !important;
            max-width: 52vw !important;
          }
          .viewerMeta { font-size: 9px !important; letter-spacing: .18em !important; }
          .viewerAbout { max-width: 380px !important; padding-left: .9rem !important; }
          .viewerAbout p { font-size: 9px !important; line-height: 1.45 !important; }
          .viewerClose { height: 2.7rem !important; width: 2.7rem !important; font-size: 1.9rem !important; }
          .viewerStage { padding: .6rem !important; }
          .viewerImageFrame {
            width: min(88vw, 1040px) !important;
            max-height: calc(100vh - 185px) !important;
            border-radius: 1.6rem !important;
          }
          .viewerArrow { height: 5rem !important; width: 2.7rem !important; font-size: 3.6rem !important; }
          .viewerControls { padding: .4rem .5rem !important; }
        }
        @media (min-width: 1100px) {
          .viewerTitle { font-size: clamp(34px, 4.6vw, 58px) !important; max-width: 46vw !important; }
          .viewerImageFrame { width: min(86vw, 1120px) !important; max-height: calc(100vh - 170px) !important; }
        }
        @media (max-width: 1023px) {
          .desktopGalleryTile:nth-child(odd) {
            transform: translateY(8px) rotate(-1.5deg);
          }
          .desktopGalleryTile:nth-child(even) {
            transform: translateY(-5px) rotate(1.3deg);
          }
          .desktopGalleryTile:nth-child(3n) {
            transform: translateY(4px) rotate(1.8deg);
          }
          .desktopGalleryTile:nth-child(4n) {
            transform: translateY(-3px) rotate(-1.2deg);
          }
        }
        @media (min-width: 640px) and (max-width: 1023px) {
          .desktopGalleryGrid {
            gap: .75rem !important;
          }
          .desktopGalleryTile:nth-child(odd),
          .desktopGalleryTile:nth-child(even),
          .desktopGalleryTile:nth-child(3n),
          .desktopGalleryTile:nth-child(4n) {
            transform: rotate(var(--tile-tilt, 0deg));
          }
          .desktopGalleryTile:nth-child(1) { --tile-tilt: -1.8deg; }
          .desktopGalleryTile:nth-child(2) { --tile-tilt: 1.4deg; }
          .desktopGalleryTile:nth-child(3) { --tile-tilt: -1deg; }
          .desktopGalleryTile:nth-child(4) { --tile-tilt: 1.7deg; }
          .desktopGalleryTile:nth-child(5) { --tile-tilt: -1.3deg; }
          .desktopGalleryTile:nth-child(6) { --tile-tilt: 1.1deg; }
        }
        @media (min-width: 1024px) {
          main { padding-top: .65rem !important; padding-bottom: .65rem !important; }
          .desktopHeader { padding-bottom: .45rem !important; }
          .desktopMainStack {
            min-height: calc(100vh - 160px) !important;
            max-width: 1220px !important;
            display: grid !important;
            grid-template-columns: 170px minmax(0, 1fr) 170px !important;
            grid-template-rows: minmax(0, 1fr) !important;
            align-items: center !important;
            gap: 1rem !important;
            padding-top: .7rem !important;
            padding-bottom: .35rem !important;
          }
          .desktopFeaturePanel {
            display: grid !important;
            grid-template-rows: minmax(0, 1fr) auto !important;
            grid-column: 2 !important;
            grid-row: 1 / span 2 !important;
            min-height: 0 !important;
            height: clamp(480px, calc(100vh - 245px), 680px) !important;
          }
          .desktopFeaturePanel > div:first-child { height: 100% !important; }
          .desktopFeaturePanel img,
          .desktopFeaturePanel video { object-fit: cover !important; object-position: center 68% !important; }
          .featureImageArea { height: 100% !important; }
          .mainFeatureImage {
            height: 100% !important;
            width: 100% !important;
            object-fit: cover !important;
            object-position: center 68% !important;
          }
          .desktopFeaturePanel .desktopFeatureTitle { font-size: clamp(42px, 6vw, 86px) !important; line-height: .72 !important; }
          .desktopFeaturePanel .desktopFeatureDesc {
            margin-top: .5rem !important;
            max-width: 38rem !important;
            padding: .38rem .6rem !important;
            font-size: 10px !important;
            line-height: 1.45 !important;
          }
          .desktopGalleryFooter { display: contents !important; }
          .desktopGalleryFooterHeader { display: none !important; }
          .desktopGalleryGrid { display: contents !important; }
          .desktopGalleryTile {
            width: 142px !important;
            height: 142px !important;
            aspect-ratio: 1 / 1 !important;
            border-radius: 1.35rem !important;
            overflow: hidden !important;
            align-self: center !important;
            justify-self: center !important;
            box-shadow: 0 18px 45px rgba(0,0,0,.07) !important;
          }
          .desktopGalleryTile > div { border-radius: 1.05rem !important; }
          .desktopGalleryTile:nth-child(1) { grid-column: 1 !important; grid-row: 1 !important; align-self: start !important; transform: translateY(8px) rotate(-4deg) !important; }
          .desktopGalleryTile:nth-child(2) { grid-column: 1 !important; grid-row: 1 !important; align-self: center !important; transform: translateY(0) rotate(2.5deg) !important; }
          .desktopGalleryTile:nth-child(3) { grid-column: 1 !important; grid-row: 1 !important; align-self: end !important; transform: translateY(-8px) rotate(-2deg) !important; }
          .desktopGalleryTile:nth-child(4) { grid-column: 3 !important; grid-row: 1 !important; align-self: start !important; transform: translateY(8px) rotate(4deg) !important; }
          .desktopGalleryTile:nth-child(5) { grid-column: 3 !important; grid-row: 1 !important; align-self: center !important; transform: translateY(0) rotate(-2.5deg) !important; }
          .desktopGalleryTile:nth-child(6) { grid-column: 3 !important; grid-row: 1 !important; align-self: end !important; transform: translateY(-8px) rotate(2deg) !important; }
          .desktopGalleryTile .tileTitle { font-size: clamp(12px, 1vw, 15px) !important; line-height: .82 !important; }
        }
        @media (min-width: 1024px) and (max-height: 760px) {
          .desktopMainStack {
            min-height: calc(100vh - 135px) !important;
            grid-template-columns: 145px minmax(0, 1fr) 145px !important;
            gap: .75rem !important;
            padding-top: .45rem !important;
          }
          .desktopFeaturePanel { height: clamp(390px, calc(100vh - 185px), 545px) !important; }
          .desktopGalleryTile { width: 122px !important; height: 122px !important; }
          .desktopFeaturePanel .desktopFeatureTitle { font-size: clamp(34px, 5vw, 68px) !important; }
        }
      `}</style>

      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_10%_10%,rgba(255,63,157,.06),transparent_20%),radial-gradient(circle_at_86%_12%,rgba(255,232,107,.12),transparent_22%),radial-gradient(circle_at_76%_80%,rgba(242,198,181,.14),transparent_18%),linear-gradient(180deg,#ffffff_0%,#fbfbfc_100%)]" />
      <div className="pointer-events-none fixed inset-0 opacity-[.09] bg-[radial-gradient(circle_at_22%_20%,rgba(255,63,157,.25),transparent_12%),radial-gradient(circle_at_78%_72%,rgba(0,0,0,.10),transparent_14%)] mix-blend-multiply" />

      <main className="relative z-10 min-h-screen p-3 md:p-5">
        <header className="desktopHeader relative pb-3 text-center">
          <HeaderBanner />
        </header>

        <section
          ref={featureRef}
          className="desktopMainStack mx-auto grid min-h-[calc(100vh-190px)] max-w-[1280px] gap-3 py-3 scroll-mt-3"
        >
          <section
            className={
              hasUnlockedEnter
                ? "desktopFeaturePanel relative grid min-h-[520px] grid-rows-[1fr_auto] overflow-visible bg-transparent p-3"
                : "desktopFeaturePanel relative overflow-visible bg-transparent"
            }
          >
            <div
              className={`featureImageArea relative overflow-hidden border-black/10 ${
                hasUnlockedEnter ? "rounded-[2.2rem] border" : "rounded-none border-0"
              }`}
            >
              {isVideoSet ? (
                <LoopingPreviewVideo
                  src={active.preview}
                  className="mainFeatureImage block h-auto w-full select-none"
                />
              ) : (
                <img
                  src={mainHeroImage || activeThumbs[0]}
                  alt={active.title}
                  className="mainFeatureImage block h-auto w-full select-none"
                  draggable={false}
                />
              )}

              {hasUnlockedEnter && (
                <>
                  <button
                    onClick={() => openSet(selected)}
                    className="desktopEnterButton font-mono font-black uppercase tracking-[.18em]"
                  >
                    {isVideoSet ? "enter" : "enter set"}
                  </button>
                  <div className="desktopFeatureOverlay absolute bottom-4 left-4 right-4 z-30 grid gap-4 md:grid-cols-[1fr_auto] md:items-end">
                    <div>
                      <h1 className="desktopFeatureTitle glitchTitle m-0 max-w-4xl text-[clamp(52px,11vw,142px)] font-black uppercase leading-[.73] tracking-[-.09em] text-[#ff3f9d]">
                        {active.title}
                      </h1>
                      <p className="desktopFeatureDesc mt-3 inline-block max-w-2xl border border-black/10 bg-white/85 px-3 py-2 font-mono text-[12px] uppercase leading-relaxed tracking-[.08em] text-zinc-800 backdrop-blur md:text-[12px]">
                        {active.desc}
                      </p>
                    </div>
                    <button
                      onClick={() => openSet(selected)}
                      className="bg-black px-7 py-4 font-mono text-xs font-black uppercase tracking-[.18em] text-white transition hover:bg-[#ff3f9d] md:hidden"
                    >
                      {isVideoSet ? "enter" : "enter set"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </section>

          <section className="desktopGalleryFooter min-h-0">
            <div className="desktopGalleryFooterHeader mb-2 flex items-center justify-between font-mono text-[10px] uppercase tracking-[.18em] text-zinc-500">
              <span></span>
              
            </div>
            <div className="desktopGalleryGrid grid max-h-none grid-cols-2 gap-2 overflow-visible pr-0 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
              {visible.map((set) => {
                const realIndex = sets.findIndex((item) => item.title === set.title);
                const isActive = realIndex === selected && chosenSetIndex !== null;
                const tileSrc = set.thumbs?.[0] || set.images?.[0];

                return (
                  <button
                    key={set.title}
                    onClick={() => selectSet(realIndex)}
                    onDoubleClick={() => openSet(realIndex)}
                    className={`desktopGalleryTile tile aspect-square overflow-hidden border p-1 text-left transition hover:scale-[.98] ${
                      isActive
                        ? "border-[#ff3f9d] bg-[#ff3f9d]/10"
                        : "border-black/10 bg-white/55 hover:border-[#ff3f9d]"
                    }`}
                  >
                    <div className="relative h-full w-full overflow-hidden bg-white">
                      {set.type === "video" ? (
                        <>
                          <LoopingPreviewVideo
                            src={set.preview}
                            className="h-full w-full object-cover"
                          />
                          <div className="absolute left-1 top-1 z-40 bg-black/80 px-2 py-1 font-mono text-[7px] font-black uppercase tracking-[.16em] text-white">
                            video
                          </div>
                        </>
                      ) : (
                        <ImageSurface
                          key={`${set.code}-tile`}
                          src={tileSrc}
                          alt={set.title}
                          tiny
                          fit="cover"
                        />
                      )}
                      <div className="absolute inset-x-1 bottom-1 z-30 bg-white/80 p-1 backdrop-blur">
                        <div className="tileTitle text-[clamp(11px,1.7vw,16px)] font-black uppercase leading-[.82] tracking-[-.05em] text-black">
                          {set.title}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        </section>
      </main>

      {videoViewer && (
        <section className="fixed inset-0 z-50 bg-white p-3 md:p-5">
          <div className="grid h-full grid-rows-[auto_1fr] overflow-hidden bg-white/80 shadow-[0_30px_100px_rgba(0,0,0,.08)]">
            <div className="flex items-start justify-between gap-4 p-3">
              <div className="min-w-0">
                <h2 className="glitchTitle mt-0 text-[clamp(44px,12vw,120px)] font-black uppercase leading-[.68] tracking-[-.1em] text-[#ff3f9d] md:text-[clamp(44px,7vw,86px)]">
                  {active.title}
                </h2>
              </div>
              <button
                onClick={() => setVideoViewer(false)}
                className="grid h-12 w-12 shrink-0 place-items-center border border-black/10 bg-white text-3xl transition hover:bg-[#ff3f9d] hover:text-white"
              >
                ×
              </button>
            </div>
            <div className="grid min-h-0 place-items-center bg-white p-3 md:p-5">
              <div
                className="relative max-h-full w-[min(92vw,540px)] overflow-hidden bg-black shadow-[0_40px_120px_rgba(0,0,0,.12)]"
                style={{ aspectRatio: active.aspectRatio || "100 / 152.04" }}
              >
                <iframe
                  src={active.embed}
                  title={active.title}
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  className="absolute inset-0 h-full w-full border-0"
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {viewer && (
        <section className="viewerShell fixed inset-0 z-50 bg-white p-3 md:p-5">
          <div className="viewerBox grid h-full grid-rows-[auto_1fr_auto] overflow-hidden border border-black/10 bg-white/80 shadow-[0_30px_100px_rgba(0,0,0,.08)]">
            <div className="viewerHeader flex items-start justify-between gap-4 border-b border-black/10 p-3">
              <div className="viewerHeaderInner flex min-w-0 flex-1 items-start gap-6 max-lg:flex-col">
                <div className="min-w-0">
                  <div className="viewerMeta font-mono text-[10px] uppercase tracking-[.2em] text-[#ff3f9d]">
                    file {currentImageIndex + 1} of {activeImageCount}
                  </div>
                  <h2 className="viewerTitle glitchTitle mt-1 text-[clamp(44px,12vw,150px)] font-black uppercase leading-[.68] tracking-[-.1em] text-[#ff3f9d]">
                    {active.title}
                  </h2>
                </div>
                <div className="viewerAbout mt-2 max-w-md border-l border-black/10 pl-4 text-left max-lg:border-l-0 max-lg:border-t max-lg:pl-0 max-lg:pt-3">
                  <div className="mb-2 font-mono text-[10px] uppercase tracking-[.18em] text-zinc-500">
                    about this set
                  </div>
                  <p className="m-0 font-mono text-[11px] uppercase leading-relaxed tracking-[.08em] text-zinc-700">
                    {active.about || active.desc}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setViewer(false)}
                className="viewerClose grid h-12 w-12 shrink-0 place-items-center border border-black/10 bg-white text-3xl transition hover:bg-[#ff3f9d] hover:text-white"
              >
                ×
              </button>
            </div>

            <div className="viewerStage relative grid min-h-0 place-items-center overflow-hidden p-3">
              <button
                onClick={() => move(-1)}
                className="viewerArrow absolute left-3 top-1/2 z-40 grid h-24 w-12 -translate-y-1/2 place-items-center border border-black/10 bg-white/70 text-6xl transition hover:bg-[#ff3f9d] hover:text-white"
              >
                ‹
              </button>
              <div className="viewerImageFrame h-full max-h-[72vh] w-[min(78vw,820px)] overflow-hidden rounded-[2.5rem] border border-black/10 shadow-[0_40px_120px_rgba(0,0,0,.10)]">
                <ImageSurface
                  key={`${active.code}-viewer-${currentImageIndex}`}
                  src={viewerImage}
                  fallbackSrc={activeThumbs[currentImageIndex]}
                  alt={`${active.title} ${currentImageIndex + 1}`}
                  active={false}
                  fit="contain"
                  zoom={zoom}
                  pannable
                />
              </div>
              <button
                onClick={() => move(1)}
                className="viewerArrow absolute right-3 top-1/2 z-40 grid h-24 w-12 -translate-y-1/2 place-items-center border border-black/10 bg-white/70 text-6xl transition hover:bg-[#ff3f9d] hover:text-white"
              >
                ›
              </button>
            </div>

            <div className="viewerControls flex items-center gap-3 overflow-x-auto border-t border-black/10 p-2">
              <div className="hidden shrink-0 items-center gap-2 border-r border-black/10 pr-3 font-mono text-[10px] uppercase tracking-[.16em] text-zinc-500 md:flex">
                <span>zoom</span>
                <input
                  type="range"
                  min="1"
                  max="3"
                  step="0.05"
                  value={zoom}
                  onChange={(event) => setZoom(Number(event.target.value))}
                  className="w-28 accent-[#ff3f9d]"
                />
                <span className="w-8 text-[#ff3f9d]">{Math.round(zoom * 100)}%</span>
                <button
                  onClick={() => setZoom(1)}
                  className="border border-black/10 bg-white px-2 py-1 text-[9px] uppercase tracking-[.14em] text-black transition hover:bg-[#ff3f9d] hover:text-white"
                >
                  reset
                </button>
              </div>

              {Array.from({ length: activeImageCount }, (_, index) => {
                const thumbSrc = activeThumbs[index] || activeImages[index];
                return (
                  <button
                    key={index}
                    onClick={(event) => {
                      event.preventDefault();
                      chooseImage(index);
                    }}
                    className={`h-16 w-16 shrink-0 overflow-hidden border transition ${
                      currentImageIndex === index
                        ? "border-[#ff3f9d] opacity-100"
                        : "border-black/10 opacity-55 hover:opacity-100"
                    }`}
                  >
                    <ImageSurface
                      key={`${active.code}-thumb-${index}`}
                      src={thumbSrc}
                      alt={`${active.title} thumbnail ${index + 1}`}
                      tiny
                      fit="cover"
                    />
                  </button>
                );
              })}
              <div className="ml-auto hidden font-mono text-[10px] uppercase tracking-[.18em] text-zinc-500 md:block">
                zoom / drag / image archive active
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
